import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { useState } from "react";

import UserSignalIcon from "../ui/UserSignalIcon.svg";

interface CredentialsStatusDialogProps {
  credentials: Partial<UserCredentialsMessage>;
  newStatus: "ACTIVE" | "INACTIVE";
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function CredentialsStatusDialog({
  credentials,
  newStatus,
  open,
  onClose,
  onConfirm,
}: CredentialsStatusDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } catch {
      // Error already handled by parent (snackbar shown)
    } finally {
      setIsSubmitting(false);
    }
  };

  const label = newStatus === "INACTIVE" ? "Inactive" : "Active";
  const credentialName = credentials.sourceName || credentials.name;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="credential-status-dialog-title"
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "735px",
          width: "100%",
          padding: "24px 36px",
          margin: "auto",
          borderRadius: "12px",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#F2F4F7",
              width: "48px",
              height: "48px",
              borderRadius: "24px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "8px solid #F9FAFB",
            }}
          >
            <img
              src={UserSignalIcon}
              alt="Credentials Icon"
              width="24"
              height="24"
            />
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: "#475467" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogTitle
          id="credential-status-dialog-title"
          sx={{
            fontWeight: 600,
            color: "#303030",
            fontSize: "18px",
            paddingX: 0,
          }}
        >
          Set {label}
        </DialogTitle>
      </Box>
      <DialogContent sx={{ padding: 0 }}>
        <DialogContentText>
          Set {credentialName} to {label}?
        </DialogContentText>
        <DialogActions>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "12px",
            }}
          >
            <Button
              fullWidth
              sx={{ borderRadius: "8px" }}
              onClick={onClose}
              color="primary"
              data-testid="cancel"
            >
              Cancel
            </Button>
            <Button
              disabled={isSubmitting}
              fullWidth
              sx={{ borderRadius: "8px" }}
              color="primary"
              variant="contained"
              data-testid="confirmStatusChange"
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Box>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
